// store/auth.ts — Auth + PIN + session state

import { create } from 'zustand';
import {
  signInWithPopup, signOut as firebaseSignOut,
  onAuthStateChanged, updateProfile,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { setupPin, setupPinWithExistingKey, unlockWithPin, unlockWithRecoveryCode, isBiometricAvailable, enrollBiometric, verifyBiometric } from '../lib/pin';
import { getPinState, putPinState, getSettings, putSettings, clearAllUserData } from '../lib/db/queries';
import { migrateLegacyData } from '../lib/db/migrations';
import { generateRecoveryCode } from '../lib/recovery';
import { importKey } from '../lib/crypto';
import type { UserSession, PinState } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive a stable local UID from an email address */
function deriveLocalUid(email: string): string {
  return 'local_' + email.replace(/[^a-zA-Z0-9]/g, '');
}

/** Map Firebase Auth error codes to user-friendly messages */
function mapFirebaseAuthError(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Try signing in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'No internet connection. Please check your network and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    default:
      return 'Authentication failed. Please try again.';
  }
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface AuthStore {
  // Auth state
  firebaseUser: User | { uid: string; email: string | null; displayName: string | null } | null;
  session: UserSession | null;
  masterKey: CryptoKey | null;
  isLoading: boolean;
  isInitialized: boolean;
  tempRecoveryCode: string | null;

  // Biometric availability
  biometricAvailable: boolean;

  // Actions
  initialize: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUpWithEmailFirebase: (email: string, password: string) => Promise<void>;
  signInWithEmailFirebase: (email: string, password: string) => Promise<void>;
  /** Legacy local-only signup — kept as fallback for offline-first PWA mode */
  signUpWithEmailLocal: (email: string) => Promise<void>;
  /** Legacy local-only signin — kept as fallback for offline-first PWA mode */
  signInWithEmailLocal: (email: string) => Promise<void>;
  sendPinResetEmail: (email: string) => Promise<void>;
  resetPinWithRecoveryCode: (recoveryCode: string, newPin: string) => Promise<boolean>;
  setRecoveryCodeSaved: (code?: string) => Promise<void>;
  signOut: () => Promise<void>;
  signOutAndClearData: (backupFirst?: () => Promise<void>) => Promise<void>;
  setupUserPin: (pin: string) => Promise<void>;
  unlockWithUserPin: (pin: string) => Promise<boolean>;
  enrollUserBiometric: () => Promise<void>;
  disableUserBiometric: () => Promise<void>;
  unlockWithUserBiometric: () => Promise<boolean>;
  setOnboardingComplete: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  uploadLocalMasterKey: (pin: string) => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  firebaseUser: null,
  session: null,
  masterKey: null,
  isLoading: true,
  isInitialized: false,
  biometricAvailable: false,
  tempRecoveryCode: null,

  initialize: async () => {
    isBiometricAvailable().then(bioAvail => {
      set({ biometricAvailable: bioAvail });
    });

    // Check local user in localStorage first
    const localUserJson = localStorage.getItem('sortednow_local_user');
    if (localUserJson) {
      try {
        const mockUser = JSON.parse(localUserJson);

        // Guard: validate shape — if corrupted, clear and fall through to Firebase
        if (!mockUser?.uid || !mockUser?.email) {
          console.warn('[Auth] Corrupted local user in localStorage — clearing');
          localStorage.removeItem('sortednow_local_user');
        } else {
          const pinState = await getPinState(mockUser.uid);
          const settings = await getSettings(mockUser.uid);

          const session: UserSession = {
            uid: mockUser.uid,
            email: mockUser.email,
            displayName: mockUser.displayName,
            photoURL: null,
            pinSetup: !!pinState,
            biometricEnrolled: !!pinState?.credentialId,
            onboardingComplete: settings.onboardingComplete,
            recoveryCodeSaved: pinState?.recoveryCodeSaved,
            isLocal: true,
          };

          set({
            firebaseUser: mockUser as { uid: string; email: string | null; displayName: string | null },
            session,
            isLoading: false,
            isInitialized: true,
          });
          return;
        }
      } catch (e) {
        console.error('[Auth] Failed to initialize local user — clearing localStorage:', e);
        localStorage.removeItem('sortednow_local_user');
        // Fall through to Firebase auth check below
      }
    }

    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        set({ firebaseUser: null, session: null, masterKey: null, isLoading: false, isInitialized: true });
        return;
      }

      const pinState = await getPinState(user.uid);
      const settings = await getSettings(user.uid);

      const session: UserSession = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        pinSetup: !!pinState,
        biometricEnrolled: !!pinState?.credentialId,
        onboardingComplete: settings.onboardingComplete,
        recoveryCodeSaved: pinState?.recoveryCodeSaved,
      };

      set({
        firebaseUser: user,
        session,
        isLoading: false,
        isInitialized: true,
        // masterKey remains null until PIN/biometric is verified
      });
    });
  },

  signInWithGoogle: async () => {
    set({ isLoading: true });
    try {
      localStorage.removeItem('sortednow_local_user');
      await signInWithPopup(auth, googleProvider);
    } finally {
      set({ isLoading: false });
    }
  },

  signUpWithEmailFirebase: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      // Clear any stale local user to avoid conflicts
      localStorage.removeItem('sortednow_local_user');

      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const user = credential.user;

      // Generate recovery code for the new account
      const recoveryCode = generateRecoveryCode();

      const session: UserSession = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || email.split('@')[0],
        photoURL: null,
        pinSetup: false,
        biometricEnrolled: false,
        onboardingComplete: false,
        recoveryCodeSaved: false,
        isLocal: false,
      };

      set({
        firebaseUser: user,
        session,
        tempRecoveryCode: recoveryCode,
        isLoading: false,
      });
    } catch (err: any) {
      set({ isLoading: false });
      const code = err?.code ?? '';
      throw new Error(mapFirebaseAuthError(code));
    }
  },

  signInWithEmailFirebase: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      localStorage.removeItem('sortednow_local_user');
      await signInWithEmailAndPassword(auth, email, password);
      // Session is set reactively by onAuthStateChanged in initialize()
    } catch (err: any) {
      set({ isLoading: false });
      const code = err?.code ?? '';
      throw new Error(mapFirebaseAuthError(code));
    }
    // isLoading will be cleared by onAuthStateChanged handler
  },

  signUpWithEmailLocal: async (email: string) => {
    set({ isLoading: true });
    try {
      const uid = deriveLocalUid(email);
      const mockUser = {
        uid,
        email,
        displayName: email.split('@')[0],
      };

      const recoveryCode = generateRecoveryCode();

      localStorage.setItem('sortednow_local_user', JSON.stringify(mockUser));

      const session: UserSession = {
        uid,
        email,
        displayName: mockUser.displayName,
        photoURL: null,
        pinSetup: false,
        biometricEnrolled: false,
        onboardingComplete: false,
        recoveryCodeSaved: false,
        isLocal: true,
      };

      set({
        firebaseUser: mockUser as { uid: string; email: string | null; displayName: string | null },
        session,
        tempRecoveryCode: recoveryCode,
        isLoading: false,
      });
    } catch (e) {
      set({ isLoading: false });
      throw e;
    }
  },

  signInWithEmailLocal: async (email: string) => {
    set({ isLoading: true });
    try {
      const uid = deriveLocalUid(email);
      const mockUser = {
        uid,
        email,
        displayName: email.split('@')[0],
      };

      const pinState = await getPinState(uid);
      const settings = await getSettings(uid);

      localStorage.setItem('sortednow_local_user', JSON.stringify(mockUser));

      const session: UserSession = {
        uid,
        email,
        displayName: mockUser.displayName,
        photoURL: null,
        pinSetup: !!pinState,
        biometricEnrolled: !!pinState?.credentialId,
        onboardingComplete: settings.onboardingComplete,
        recoveryCodeSaved: pinState?.recoveryCodeSaved,
        isLocal: true,
      };

      set({
        firebaseUser: mockUser as { uid: string; email: string | null; displayName: string | null },
        session,
        isLoading: false,
      });
    } catch (e) {
      set({ isLoading: false });
      throw e;
    }
  },

  sendPinResetEmail: async (email: string) => {
    const session = get().session;
    // For Firebase users — send the real Firebase password reset email
    if (!session?.isLocal) {
      await firebaseSendPasswordResetEmail(auth, email);
    }
    // For local-only users there's no email infrastructure; callers handle the UX.
  },

  resetPinWithRecoveryCode: async (recoveryCode: string, newPin: string) => {
    const user = get().firebaseUser;
    if (!user) return false;

    const pinState = await getPinState(user.uid);
    if (!pinState) return false;

    try {
      const masterKey = await unlockWithRecoveryCode(recoveryCode, user.uid, pinState);

      const newPinState = await setupPinWithExistingKey(newPin, user.uid, masterKey, recoveryCode);
      newPinState.recoveryCodeSaved = true;
      await putPinState(user.uid, newPinState);

      set({
        masterKey,
        session: get().session ? { ...get().session!, pinSetup: true, recoveryCodeSaved: true } : null,
      });
      return true;
    } catch (e) {
      console.error('[Auth] Recovery PIN reset failed:', e);
      return false;
    }
  },

  setRecoveryCodeSaved: async (code?: string) => {
    const user = get().firebaseUser;
    if (!user) return;
    const pinState = await getPinState(user.uid);
    if (pinState) {
      pinState.recoveryCodeSaved = true;
      await putPinState(user.uid, pinState);
    }
    set(state => ({
      tempRecoveryCode: code || state.tempRecoveryCode,
      session: state.session ? { ...state.session, recoveryCodeSaved: true } : null,
    }));
  },

  signOut: async () => {
    localStorage.removeItem('sortednow_local_user');
    await firebaseSignOut(auth);
    set({ firebaseUser: null, session: null, masterKey: null, tempRecoveryCode: null });
  },

  signOutAndClearData: async (backupFirst?: () => Promise<void>) => {
    const uid = get().session?.uid;

    // Run optional pre-wipe backup (e.g. Drive sync)
    if (backupFirst) {
      try {
        await backupFirst();
      } catch (e) {
        console.warn('[Auth] Pre-wipe backup failed:', e);
        // Continue with sign-out regardless
      }
    }

    // Wipe all IndexedDB data for this user
    if (uid) {
      try {
        await clearAllUserData(uid);
      } catch (e) {
        console.error('[Auth] Failed to clear local data:', e);
      }
    }

    localStorage.removeItem('sortednow_local_user');
    await firebaseSignOut(auth);
    set({ firebaseUser: null, session: null, masterKey: null, tempRecoveryCode: null });
  },

  setupUserPin: async (pin: string) => {
    const user = get().firebaseUser;
    if (!user) throw new Error('Not authenticated');

    const tempRecoveryCode = get().tempRecoveryCode;

    // If the user already has an existing masterKey in memory (e.g. came back through
    // onboarding or recovery-code page on an account that already has a backup), reuse it.
    // This prevents a new random masterKey from being generated which would invalidate
    // any existing Drive backup.
    const existingMasterKey = get().masterKey;
    let pinState: import('../types').PinState;
    let masterKey: CryptoKey;

    if (existingMasterKey) {
      // Re-wrap the existing master key under the new PIN
      pinState = await setupPinWithExistingKey(pin, user.uid, existingMasterKey, tempRecoveryCode || undefined);
      masterKey = existingMasterKey;
    } else {
      // No existing key in memory — check if the server already has a pinState we can recover
      const existingPinState = await getPinState(user.uid);
      if (existingPinState) {
        try {
          // Try to recover the existing masterKey by decrypting the stored pinState with the new pin
          // This handles the edge case where the user sets the same pin twice
          const recoveredKey = await unlockWithPin(pin, user.uid, existingPinState);
          pinState = await setupPinWithExistingKey(pin, user.uid, recoveredKey, tempRecoveryCode || undefined);
          masterKey = recoveredKey;
        } catch {
          // Existing pinState can't be unlocked with this pin (different pin) — generate new key
          const result = await setupPin(pin, user.uid, tempRecoveryCode || undefined);
          pinState = result.pinState;
          masterKey = result.masterKey;
        }
      } else {
        // No existing pinState at all — first time setup
        const result = await setupPin(pin, user.uid, tempRecoveryCode || undefined);
        pinState = result.pinState;
        masterKey = result.masterKey;
      }
    }

    if (tempRecoveryCode) {
      pinState.recoveryCodeSaved = true;
    }

    await putPinState(user.uid, pinState);

    // Run legacy migration after first PIN setup
    try {
      await migrateLegacyData(user.uid);
    } catch (e) {
      console.warn('[Migration] Failed:', e);
    }

    set(state => ({
      masterKey,
      tempRecoveryCode: null,
      session: state.session ? { ...state.session, pinSetup: true, recoveryCodeSaved: pinState.recoveryCodeSaved } : null,
    }));
  },

  unlockWithUserPin: async (pin: string) => {
    const user = get().firebaseUser;
    if (!user) return false;

    const pinState = await getPinState(user.uid);
    if (!pinState) return false;

    try {
      const masterKey = await unlockWithPin(pin, user.uid, pinState);
      set({ masterKey });
      return true;
    } catch {
      return false;
    }
  },

  enrollUserBiometric: async () => {
    const user = get().firebaseUser;
    const masterKey = get().masterKey;
    if (!user || !masterKey) throw new Error('Not authenticated or PIN not set');

    const credentialId = await enrollBiometric(user.uid, masterKey);
    const pinState = await getPinState(user.uid);
    if (!pinState) throw new Error('PIN state not found');

    const updated: PinState = { ...pinState, credentialId };
    await putPinState(user.uid, updated);

    set(state => ({
      session: state.session ? { ...state.session, biometricEnrolled: true } : null,
    }));
  },

  disableUserBiometric: async () => {
    const user = get().firebaseUser;
    if (!user) return;

    const pinState = await getPinState(user.uid);
    if (!pinState) return;

    const updated = { ...pinState };
    delete updated.credentialId;
    await putPinState(user.uid, updated);

    set(state => ({
      session: state.session ? { ...state.session, biometricEnrolled: false } : null,
    }));
  },

  unlockWithUserBiometric: async () => {
    const user = get().firebaseUser;
    if (!user) return false;

    const pinState = await getPinState(user.uid);
    if (!pinState?.credentialId) return false;

    const keyBytes = await verifyBiometric(pinState.credentialId);
    if (!keyBytes) return false;

    try {
      const masterKey = await importKey(keyBytes);
      set({ masterKey });
      return true;
    } catch (e) {
      console.error('[Auth] Biometric key import failed:', e);
      return false;
    }
  },

  setOnboardingComplete: async () => {
    const user = get().firebaseUser;
    if (!user) return;

    const settings = await getSettings(user.uid);
    await putSettings({ ...settings, onboardingComplete: true });

    set(state => ({
      session: state.session ? { ...state.session, onboardingComplete: true } : null,
    }));
  },

  updateDisplayName: async (name: string) => {
    const user = get().firebaseUser;
    if (!user) return;

    const isLocal = get().session?.isLocal;

    if (isLocal) {
      const mockUser = {
        uid: user.uid,
        email: user.email,
        displayName: name,
      };
      localStorage.setItem('sortednow_local_user', JSON.stringify(mockUser));
      set(state => ({
        firebaseUser: mockUser,
        session: state.session ? { ...state.session, displayName: name } : null,
      }));
    } else {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: name });

        // Push settings to remote API if applicable to sync display name
        const settings = await getSettings(user.uid);
        await putSettings({ ...settings, driveName: name });

        set(state => ({
          firebaseUser: auth.currentUser,
          session: state.session ? { ...state.session, displayName: name } : null,
        }));
      }
    }
  },

  uploadLocalMasterKey: async (pin: string) => {
    const user = get().firebaseUser;
    if (!user) throw new Error('Not authenticated');

    const masterKey = get().masterKey;
    if (!masterKey) throw new Error('No local master key found');

    const tempRecoveryCode = get().tempRecoveryCode || generateRecoveryCode();
    const pinState = await setupPinWithExistingKey(pin, user.uid, masterKey, tempRecoveryCode);
    pinState.recoveryCodeSaved = true;

    await putPinState(user.uid, pinState);

    set(state => ({
      tempRecoveryCode: null,
      session: state.session ? { ...state.session, pinSetup: true, recoveryCodeSaved: true } : null,
    }));
  },
}));
